import { ref } from "vue"
import { SessionInfo } from "./personSessionData"
import { appAuth } from "../auth/appAuth"
// import { OktaAuth } from '@okta/okta-auth-js'
import { authProvider } from "./authProviderFactory"
import { rememberPostLoginPath } from "./postLoginRedirect"
import { useThemeManagerStore } from "@/store/themeManagerStore"
export abstract class routeGuards {
	public static requiresAdmin(to, from, next) {
		if (SessionInfo.currentUserIsAdmin()) {
			next()
		} else {
			next("/")
		}
	}

	public static requiresOktaLogin(to, from, next) {
		// Written down first, before anything reads the session: SessionInfo.currentUser() starts
		// the redirect to the identity provider by itself when there is no session, so a line
		// after it never runs. This is what brings the reader back to the page they asked for —
		// a support request opened from an email link, typically — instead of the landing page.
		// Only while signed out. This is the destination to come back to AFTER signing in, so
		// remembering it on every navigation of a signed-in reader turns it into "wherever I was
		// last", and then opening the bare domain returns them there instead of the landing page.
		// Read from storage rather than through SessionInfo: that getter starts the redirect to the
		// identity provider by itself, which is the whole reason this line comes first.
		if (!localStorage.getItem("PersonSessionData")) rememberPostLoginPath(to?.fullPath)

		//console.log("currentUser", SessionInfo.currentUser())
		if (SessionInfo.currentUser()) {
			// The destination is NOT dropped here. Letting a navigation through is not the same
			// as completing it: the router is still on the previous page, and a later navigation
			// can still overtake this one. It is dropped in router.afterEach, on arrival.
			next()
		} else if (authProvider.isLoginRedirect?.()) {
			// PKCE callback is in progress — let LoginCallback component handle it
			next()
		} else {
			authProvider.signInWithRedirect().then(async () => {
				const token = await authProvider.getAccessToken()
				console.log("routeGuards token ", token)

				if (token) {
					localStorage.setItem("token", token)

					await appAuth.loginToAppWithOktaToken(token).then((callback) => {
						if (callback != "ok") {
							sessionStorage.setItem("error", callback)
							return false
							//logout()
						} else {
							return next()
						}
					})
					return next()
				} else {
					return false
				}
			})
		}
	}

	public static async requiresOktaLoginWithTheme(to, from, next) {
		// See requiresOktaLogin: reading the session is itself what redirects, so the destination
		// has to be written down before that happens.
		// Only while signed out. This is the destination to come back to AFTER signing in, so
		// remembering it on every navigation of a signed-in reader turns it into "wherever I was
		// last", and then opening the bare domain returns them there instead of the landing page.
		// Read from storage rather than through SessionInfo: that getter starts the redirect to the
		// identity provider by itself, which is the whole reason this line comes first.
		if (!localStorage.getItem("PersonSessionData")) rememberPostLoginPath(to?.fullPath)

		//console.log("currentUser", SessionInfo.currentUser())
		if (SessionInfo.currentUser()) {
			// As in requiresOktaLogin: the destination is dropped on arrival, in router.afterEach,
			// not here where the navigation has only been allowed.

			// User is already logged in, load their theme before proceeding
			const themeStore = useThemeManagerStore()
			try {
				await themeStore.loadUserThemeOrDefault()
				// console.log("User theme loaded successfully in route guard")
				next()
			} catch (error) {
				console.warn("Failed to load user theme in route guard, proceeding with default:", error)
				next()
			}
		} else if (authProvider.isLoginRedirect?.()) {
			// PKCE callback is in progress — let LoginCallback component handle it
			next()
		} else {
			// Awaited, not started and left to run on its own.
			//
			// This method is async, so vue-router receives a promise from it, and it takes `next`
			// as well. A guard of that shape whose promise settles before `next` has been called is
			// rejected with "Invalid navigation guard" and the navigation is thrown away. Started
			// without awaiting, the sign-in below settled this guard immediately, so the jump to an
			// email link was dropped and whatever navigated next — the login screen going to /app —
			// took its place: the address of the request appeared for an instant and the reader
			// ended up on the landing page.
			//
			// Awaiting normally means never coming back, because signInWithRedirect takes the
			// browser away; when it does return, the session is there and next() is called below.
			await authProvider.signInWithRedirect()

			const token = await authProvider.getAccessToken()

			if (!token) {
				next(false)
				return
			}

			localStorage.setItem("token", token)

			const callback = await appAuth.loginToAppWithOktaToken(token)
			if (callback != "ok") {
				sessionStorage.setItem("error", callback)
				next(false)
				return
			}

			// After successful login, load user theme before navigation
			const themeStore = useThemeManagerStore()
			try {
				await themeStore.loadUserThemeOrDefault()
			} catch (error) {
				console.warn("Failed to load user theme after login, using default:", error)
			}

			next()
		}
	}
}
